// Ollama API Client
// https://github.com/ollama/ollama/blob/main/docs/api.md

import { createOllamaUtils } from './utils.js'

/**
 * Create Ollama client
 * @param {Function} httpRequest - HTTP request function
 * @returns {Object} Ollama client
 */
export function createOllamaClient(httpRequest) {
  const { requestAPI, checkAvailability } = createOllamaUtils(httpRequest)

  const generate = async ({ model, prompt, options = {}, stream = false, host }) => {
    if (!model) throw new Error('model is required')
    if (!prompt) throw new Error('prompt is required')

    return await requestAPI({
      host,
      path: '/api/generate',
      method: 'POST',
      body: { model, prompt, options, stream },
    })
  }

  const chat = async ({ model, messages, tools, think, options = {}, stream = false, host }) => {
    if (!model) throw new Error('model is required')
    if (!messages || !Array.isArray(messages) || messages.length === 0) {
      throw new Error('messages is required and must be a non-empty array')
    }

    const body = { model, messages, options, stream }
    if (tools) body.tools = tools
    if (think !== undefined) body.think = think

    return await requestAPI({
      host,
      path: '/api/chat',
      method: 'POST',
      body,
    })
  }

  const chatCompletions = async ({
    model,
    messages,
    temperature,
    max_tokens,
    tools,
    tool_choice,
    stream = false,
    host,
  }) => {
    if (!model) throw new Error('model is required')
    if (!messages || !Array.isArray(messages) || messages.length === 0) {
      throw new Error('messages is required and must be a non-empty array')
    }

    const bodyParams = { model, messages, temperature, max_tokens, tools, tool_choice, stream }
    const body = Object.fromEntries(
      Object.entries(bodyParams).filter(([_, value]) => value !== undefined),
    )

    return await requestAPI({
      host,
      path: '/v1/chat/completions',
      method: 'POST',
      body,
    })
  }

  const listModels = async ({ host } = {}) => {
    return await requestAPI({
      host,
      path: '/api/tags',
      method: 'GET',
      body: {},
    })
  }

  const showModel = async ({ model, host }) => {
    if (!model) throw new Error('model is required')

    return await requestAPI({
      host,
      path: '/api/show',
      method: 'POST',
      body: { name: model },
    })
  }

  const pullModel = async ({ model, insecure = false, stream = false, host }) => {
    if (!model) throw new Error('model is required')

    return await requestAPI({
      host,
      path: '/api/pull',
      method: 'POST',
      body: { name: model, insecure, stream },
    })
  }

  const embeddings = async ({ model, prompt, options = {}, host }) => {
    if (!model) throw new Error('model is required')
    if (!prompt) throw new Error('prompt is required')

    return await requestAPI({
      host,
      path: '/api/embeddings',
      method: 'POST',
      body: { model, prompt, options },
    })
  }

  return {
    generate,
    chat,
    chatCompletions,
    listModels,
    showModel,
    pullModel,
    embeddings,
    checkAvailability,
  }
}
